import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import {
  AppointmentAdditionalServicesField,
  AppointmentClientEmailField,
  type AppointmentCreateFormData,
  AppointmentDateTimeFields,
  AppointmentDerivedSummary,
  AppointmentNotesField,
  AppointmentPackageField,
  AppointmentPractitionerField,
  DoubleBookingConfirmDialog,
  appointmentCreateDefaults,
  appointmentCreateForm,
  appointmentCreateSchema,
  useAdditionalServices,
  useAppointmentClientEmail,
  useAppointmentPackageSelection,
  useCreateAppointmentFlow,
  useFirstServicePractitioners,
} from '@/features/appointments/create';

import {
  AppointmentResourceFields,
  useAppointmentResourceSelection,
} from '@/features/resources/booking';
import { useResourceWarningToasts } from '@/features/resources/booking';

export interface AppointmentMobileCreateDetailsFormProps {
  formId: string;
  leadId: string;
  serviceId: string;
  slotDate: Date;
  defaultDate?: string;
  defaultHour?: number;
  defaultMinute?: number;
  practitionerId?: string;
  onSuccess: () => void;
  onPendingChange?: (isPending: boolean) => void;
}

/**
 * MOBILE surface for the SHARED appointment-create core. Composes the same
 * shared fields the desktop dialog uses, laid out as a funnel step.
 *
 * The title, the colour and the end time are all DERIVED (from the service and
 * the assigned practitioner) — mobile no longer asks the user to type a title,
 * pick a colour, or snap the duration to a bucket.
 */
export function AppointmentMobileCreateDetailsForm({
  formId,
  leadId,
  serviceId,
  slotDate,
  defaultDate,
  defaultHour,
  defaultMinute,
  practitionerId,
  onSuccess,
  onPendingChange,
}: AppointmentMobileCreateDetailsFormProps) {
  const { notifyResourceWarnings, resourceOverrideDialog } =
    useResourceWarningToasts();

  const {
    submit,
    isCreating,
    context,
    conflict,
    confirmDoubleBooking,
    dismissConflict,
  } = useCreateAppointmentFlow({
    // Same warn-don't-block contract as the desktop dialog: the booking was
    // made, the room just clashes. Reported, never treated as a failure.
    // (A double-booked PRACTITIONER is the other axis and blocks until
    // confirmed — see `conflict` below.)
    onSuccess: (appointment) => {
      notifyResourceWarnings({
        response: appointment,
        appointmentId: String(appointment.id),
        window: resources.window,
      });
      onSuccess();
    },
    // Several services: rooms and the package went on the first one.
    onVisitSuccess: (visit) => {
      const first = visit.appointments[0];
      if (first) {
        notifyResourceWarnings({
          response: first,
          appointmentId: String(first.id),
          window: resources.window,
        });
      }
      onSuccess();
    },
  });

  const defaultValues = useMemo(() => {
    const values = appointmentCreateDefaults({
      timeZone: context.timeZone,
      slotDate,
      startTime:
        defaultHour !== undefined && defaultMinute !== undefined
          ? { hour: defaultHour, minute: defaultMinute }
          : undefined,
      leadId,
      serviceId,
      practitionerId,
    });
    return defaultDate ? { ...values, date: defaultDate } : values;
  }, [
    context.timeZone,
    slotDate,
    defaultDate,
    defaultHour,
    defaultMinute,
    leadId,
    serviceId,
    practitionerId,
  ]);

  const form = useForm<AppointmentCreateFormData>({
    resolver: zodResolver(appointmentCreateSchema),
    defaultValues,
  });

  useEffect(() => {
    onPendingChange?.(isCreating);
  }, [isCreating, onPendingChange]);

  // Rooms & equipment — renders nothing for an org with no resource
  // categories, so this funnel step is byte-identical for every clinic today.
  const resources = useAppointmentResourceSelection(form.control);
  // A service the customer still has in a package: "Use {package}".
  const packageService = useAppointmentPackageSelection(form.control);

  const service = context.services.find((s) => s.id === serviceId);

  // The first service's team members: only the people who perform it.
  const { eligible: firstServicePractitioners, practitionerId: firstPick } =
    useFirstServicePractitioners(form, context.practitioners);
  // Services after the first, each with its own team member.
  const additionalServices = useAdditionalServices(context.practitioners);
  // An online-only booking for a client with no email: where to send the Meet link.
  const clientEmail = useAppointmentClientEmail(
    form.control,
    context.services,
    additionalServices.items.map((item) => item.serviceId)
  );

  return (
    <form
      id={formId}
      onSubmit={form.handleSubmit(
        (values) => {
          const servicesValid = additionalServices.validate();
          if (!clientEmail.validate() || !servicesValid) return;
          submit(values, {
            resourceIds: resources.resourceIds,
            allowResourceOverbook: resources.overbooks,
            clientPackageItemId: packageService.clientPackageItemId,
            clientEmail: clientEmail.clientEmail,
            additionalServices: additionalServices.items,
          });
        },
        () => {
          additionalServices.validate();
          clientEmail.validate();
        }
      )}
      className="flex flex-col gap-4"
    >
      {/* Same confirmation the desktop dialog shows — the mobile funnel took
          the identical silent-double-booking path (ENG-792). */}
      <DoubleBookingConfirmDialog
        conflict={conflict}
        onConfirm={confirmDoubleBooking}
        onCancel={dismissConflict}
      />

      <input type="hidden" {...form.register('leadId')} />
      <input type="hidden" {...form.register('serviceId')} />

      <AppointmentDerivedSummary service={service} />

      <AppointmentPackageField selection={packageService} variant="mobile" />

      <AppointmentClientEmailField selection={clientEmail} variant="mobile" />

      <AppointmentDateTimeFields control={form.control} variant="mobile" />

      <section>
        <p className="pb-2 text-[13px] text-[#8E8E93]">
          {appointmentCreateForm.labels.practitionerId}
        </p>
        <AppointmentPractitionerField
          control={form.control}
          practitioners={firstServicePractitioners}
          variant="mobile"
        />
      </section>

      <AppointmentAdditionalServicesField
        selection={additionalServices}
        services={context.services}
        practitioners={context.practitioners}
        isLoading={context.isLoading}
        defaultPractitionerId={firstPick}
        variant="mobile"
      />

      <AppointmentResourceFields selection={resources} variant="mobile" />

      <AppointmentNotesField control={form.control} variant="mobile" />

      {resourceOverrideDialog}
    </form>
  );
}
